import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { saas, errMsg } from "@/lib/saasApi";

const STATUS_LABEL = {
  concept: { t: "Draft", c: "bg-re-soft text-re-muted" },
  preparing: { t: "Preparing", c: "bg-re-soft text-re-muted" },
  processing: { t: "Processing", c: "bg-amber-100 text-amber-700" },
  completed: { t: "Completed", c: "bg-emerald-100 text-emerald-700" },
  support_review: { t: "Under review", c: "bg-blue-100 text-blue-700" },
};

export default function AdminProjects() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refund, setRefund] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    saas.adminProjects().then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const doRefund = async () => {
    if (!reason.trim()) return toast.error("Please provide a reason.");
    setBusy(true);
    try { await saas.adminRefund(refund.id, reason.trim()); toast.success("Credit refunded."); setRefund(null); setReason(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-projects">
      <h1 className="font-heading text-2xl font-bold text-re-charcoal">Projects</h1>
      {loading ? <p className="text-sm text-re-muted">Loading…</p> : (
        <div className="overflow-x-auto rounded-2xl border border-re-line bg-white">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Project</TableHead><TableHead>Customer</TableHead><TableHead>Format</TableHead>
              <TableHead>Status</TableHead><TableHead>Provider status</TableHead><TableHead>Listing ID</TableHead><TableHead>Photos</TableHead><TableHead>Created</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => {
                const s = STATUS_LABEL[r.status] || { t: r.status, c: "bg-re-soft text-re-muted" };
                return (
                  <TableRow key={r.id} data-testid={`ap-row-${r.id}`}>
                    <TableCell className="font-medium text-re-charcoal">{r.projectName}</TableCell>
                    <TableCell className="text-re-muted">{r.organisationName}</TableCell>
                    <TableCell className="text-re-muted">{r.ratio}</TableCell>
                    <TableCell><Badge className={s.c}>{s.t}</Badge></TableCell>
                    <TableCell className="text-re-muted">{r.providerStatus || "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-re-muted">{r.providerListingId || "—"}</TableCell>
                    <TableCell className="text-re-muted">{r.photoCount}</TableCell>
                    <TableCell className="text-xs text-re-muted">{r.createdAt ? new Date(r.createdAt).toLocaleDateString("en-GB") : "—"}</TableCell>
                    <TableCell>
                      {r.providerListingId && <Button size="sm" variant="outline" className="rounded-full" data-testid={`ap-refund-${r.id}`} onClick={() => { setRefund(r); setReason(""); }}>Refund</Button>}
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-re-muted">No projects yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!refund} onOpenChange={(v) => !v && setRefund(null)}>
        <DialogContent data-testid="refund-modal">
          <DialogHeader><DialogTitle>Refund credit</DialogTitle><DialogDescription>Grants 1 project credit back to the customer. A reason is required.</DialogDescription></DialogHeader>
          <div className="space-y-1.5"><Label>Reason</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="refund-reason" className="border-re-line" placeholder="e.g. failed generation at provider" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefund(null)} className="rounded-full">Cancel</Button>
            <Button onClick={doRefund} disabled={busy} data-testid="refund-submit" className="rounded-full bg-re-red text-white hover:bg-re-deepred">{busy ? "Working…" : "Refund"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
